import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PricePerPieceTooltip } from '~/components/ui';
import type { UnitCode } from '~/graphql/graphql';
import { formatUnitPrice, priceUnitLabel } from '~/utils';

type CartItemPriceSummaryProps = {
  displayPricePerUnit: number | null;
  effectivePricePerPiece: number | null;
  sellerDisplayPricePerUnit: number | null;
  suggestedAmount: number | null;
  canClearSuggestion: boolean;
  priceUnit: UnitCode;
  currency?: string;
  onEditSuggestion: () => void;
  onClearSuggestion: () => void;
  onStartSuggestion: () => void;
};

export function CartItemPriceSummary({
  displayPricePerUnit,
  effectivePricePerPiece,
  sellerDisplayPricePerUnit,
  suggestedAmount,
  canClearSuggestion,
  priceUnit,
  currency,
  onEditSuggestion,
  onClearSuggestion,
  onStartSuggestion,
}: Readonly<CartItemPriceSummaryProps>) {
  const { t } = useTranslation('cart');
  const hasSuggestion = suggestedAmount != null;

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center justify-end gap-1">
        <p
          className={`flex items-center gap-1 text-xs ${hasSuggestion ? 'text-warning-700 font-medium' : 'text-neut-sec'}`}
        >
          {displayPricePerUnit != null
            ? `${formatUnitPrice(displayPricePerUnit, currency)}/${priceUnitLabel(priceUnit)}`
            : t('cart.price_not_available')}
          {effectivePricePerPiece != null && priceUnit === 'PCS' && (
            <PricePerPieceTooltip
              pricePerPiece={effectivePricePerPiece}
              currency={currency}
              iconOnly
              className={hasSuggestion ? 'text-warning-600' : 'text-neut-sec'}
            />
          )}
        </p>
        {hasSuggestion && (
          <button
            type="button"
            aria-label={t('cart.suggest_price_edit')}
            className="text-warning-500 hover:text-warning-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-prim rounded"
            onClick={onEditSuggestion}
          >
            <Pencil className="w-3 h-3" aria-hidden="true" />
          </button>
        )}
      </div>
      {hasSuggestion ? (
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-xs text-warning-600">
            {t('cart.suggest_price_pending')}
          </span>
          {canClearSuggestion && (
            <button
              type="button"
              title={t('cart.suggest_price_clear')}
              aria-label={t('cart.suggest_price_clear')}
              className="text-xs text-neut-400 hover:text-error-500 transition-colors leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-prim rounded"
              onClick={onClearSuggestion}
            >
              ✕
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-prim hover:text-prim/80 transition-colors mt-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-prim rounded"
          onClick={onStartSuggestion}
        >
          <Pencil className="w-3 h-3" aria-hidden="true" />
          {t('cart.suggest_price')}
        </button>
      )}
      {effectivePricePerPiece != null && priceUnit !== 'PCS' && (
        <PricePerPieceTooltip
          pricePerPiece={effectivePricePerPiece}
          currency={currency}
          className="text-xs font-semibold text-neut-sec"
        />
      )}
      {hasSuggestion && sellerDisplayPricePerUnit != null && (
        <p className="text-xs text-neut-400">
          {t('cart.seller_price_reference', {
            price: formatUnitPrice(sellerDisplayPricePerUnit, currency),
            unit: priceUnitLabel(priceUnit),
          })}
        </p>
      )}
    </div>
  );
}
